/**
 * Sistema de Logging Estruturado para o Itaúna Web
 * 
 * Implementa logs estruturados para rastreamento de operações,
 * erros e métricas do sistema.
 * 
 * Em produção, integre com serviços como:
 * - Sentry (erros)
 * - LogRocket (sessões)
 * - DataDog (métricas)
 * - CloudWatch (AWS)
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  FATAL = 'FATAL'
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  data?: any;
  context?: {
    userAgent?: string;
    url?: string;
    sessionId?: string;
  };
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private logs: LogEntry[] = [];
  private maxLogs = 100; // Mantém últimos 100 logs em memória

  /**
   * Cria uma entrada de log estruturada
   */
  private createLogEntry(
    level: LogLevel,
    message: string,
    data?: any
  ): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      data,
      context: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        sessionId: this.getSessionId()
      }
    };
  }

  /**
   * Obtém ou cria um ID de sessão
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Armazena log em memória
   */
  private storeLog(entry: LogEntry): void {
    this.logs.push(entry);
    
    // Mantém apenas os últimos logs
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  /**
   * Envia log para serviço externo (em produção)
   */
  private async sendToExternalService(entry: LogEntry): Promise<void> {
    // Em produção, envie para seu serviço de logging
    // Exemplo com Sentry:
    // if (entry.level === LogLevel.ERROR || entry.level === LogLevel.FATAL) {
    //   Sentry.captureException(entry.data?.error || new Error(entry.message), {
    //     extra: entry.data,
    //     tags: { level: entry.level }
    //   });
    // }
    
    // Exemplo com endpoint personalizado:
    // try {
    //   await fetch('/api/logs', {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify(entry)
    //   });
    // } catch (error) {
    //   console.error('Failed to send log:', error);
    // }
  }

  /**
   * Log DEBUG - Informações de depuração
   */
  debug(message: string, data?: any): void {
    const entry = this.createLogEntry(LogLevel.DEBUG, message, data);
    this.storeLog(entry);
    
    if (this.isDevelopment) {
      console.debug(`[DEBUG] ${message}`, data || '');
    }
  }

  /**
   * Log INFO - Informações gerais
   */
  info(message: string, data?: any): void {
    const entry = this.createLogEntry(LogLevel.INFO, message, data);
    this.storeLog(entry);
    
    if (this.isDevelopment) {
      console.info(`[INFO] ${message}`, data || '');
    }
    
    // Em produção, pode enviar métricas
    this.sendToExternalService(entry);
  }

  /**
   * Log WARN - Avisos
   */
  warn(message: string, data?: any): void {
    const entry = this.createLogEntry(LogLevel.WARN, message, data);
    this.storeLog(entry);
    
    if (this.isDevelopment) {
      console.warn(`[WARN] ${message}`, data || '');
    }
    this.sendToExternalService(entry);
  }

  /**
   * Log ERROR - Erros
   */
  error(message: string, data?: any): void {
    const entry = this.createLogEntry(LogLevel.ERROR, message, data);
    this.storeLog(entry);
    
    if (this.isDevelopment) {
      console.error(`[ERROR] ${message}`, data || '');
    }
    this.sendToExternalService(entry);
  }

  /**
   * Log FATAL - Erros críticos
   */
  fatal(message: string, data?: any): void {
    const entry = this.createLogEntry(LogLevel.FATAL, message, data);
    this.storeLog(entry);
    
    if (this.isDevelopment) {
      console.error(`[FATAL] ${message}`, data || '');
    }
    this.sendToExternalService(entry);
  }

  /**
   * Obtém todos os logs armazenados
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Limpa os logs armazenados
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Exporta logs como JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

// Cria instância singleton
const logger = new Logger();

// Exporta métodos de conveniência
export const logDebug = (message: string, data?: any) => logger.debug(message, data);
export const logInfo = (message: string, data?: any) => logger.info(message, data);
export const logWarn = (message: string, data?: any) => logger.warn(message, data);
export const logError = (message: string, data?: any) => logger.error(message, data);
export const logFatal = (message: string, data?: any) => logger.fatal(message, data);

export const getLogs = () => logger.getLogs();
export const clearLogs = () => logger.clearLogs();
export const exportLogs = () => logger.exportLogs();

export default logger;
